import { useState } from "react";
import { FileText, Pencil, Upload, CheckCircle2, AlertTriangle, FileOutput } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { btn, field } from "@/components/ui-kit";

const RECEIVE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/as2/receive`;
const dmy = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

const EnvBox = ({ p, env, isMgr, save }) => {
  const [f, setF] = useState(env);
  const test = env.env === "test";
  const ids = p.test_ids || {};
  return (
    <div className={`mt-3 space-y-2 rounded-sm border px-3 py-2 text-xs ${test ? "border-amber-500/40 bg-amber-500/10" : "border-slate-800 bg-[#171A21]"}`} data-testid={`partner-env-${p.code}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[10px] uppercase text-slate-500">EDI account</span>
        <span className={test ? "text-slate-500" : "font-semibold text-[#22C55E]"}>Live</span>
        <Switch checked={test} disabled={!isMgr} onCheckedChange={(v) => save({ ...f, env: v ? "test" : "live" })} data-testid={`partner-env-switch-${p.code}`} />
        <span className={test ? "font-semibold text-amber-300" : "text-slate-500"}>Test</span>
        <span className="text-slate-400" data-testid={`partner-env-note-${p.code}`}>{test ? `Files ARE sent, to ${p.name}'s test account: test EDI IDs, ISA15/UNB test flag and the test AS2 address below. Other partners stay live.` : "Production EDI IDs and AS2 address."}</span>
      </div>
      {isMgr && <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-64 flex-1"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">Test AS2 URL (blank = same as live)</span><Input value={f.test_as2_url} onChange={(e) => setF({ ...f, test_as2_url: e.target.value })} className={`h-8 ${field}`} data-testid={`partner-env-url-${p.code}`} /></label>
        <label className="w-56"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">Test AS2 ID (blank = same)</span><Input value={f.test_as2_id} onChange={(e) => setF({ ...f, test_as2_id: e.target.value })} className={`h-8 ${field}`} data-testid={`partner-env-as2id-${p.code}`} /></label>
        <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => save({ ...f, env: env.env })} data-testid={`partner-env-save-${p.code}`}>Save test address</Button>
      </div>}
      <div className="text-slate-500">Test EDI IDs used: partner {ids.partner_isa || ids.partner_unb || "- (not set, live ID used)"} · EP {ids.ep_isa || "- (live ID used)"} - set them in {p.builtin ? "Partner Settings" : "Edit"}.</div>
    </div>
  );
};

export const PartnerCard = ({ p, types, isMgr, onEdit, onChanged, onIdoc, onPricing }) => {
  const [msgs, setMsgs] = useState(p.messages || {});
  const [auto, setAuto] = useState(p.send_mode !== "manual");
  const [env, setEnv] = useState({ env: p.edi_env || "live", test_as2_url: p.test_as2_url || "", test_as2_id: p.test_as2_id || "" });
  const saveEnv = (e) => api.put(`/partners/${p.code}/env`, e)
    .then(() => { setEnv(e); toast.success(`${p.name}: ${e.env === "test" ? "TEST - files go to the test account" : "LIVE - production account"}`); onChanged(); })
    .catch((err) => toast.error(errMsg(err)));
  const setMode = (v) => {
    setAuto(v);
    api.put(`/partners/${p.code}/send-mode`, { mode: v ? "auto" : "manual" })
      .then(() => { toast.success(`${p.name}: ${v ? "messages send automatically" : "messages wait in the EDI Log for manual sending"}`); onChanged(); })
      .catch((e) => { setAuto(!v); toast.error(errMsg(e)); });
  };
  const toggle = async (k, v) => {
    const m = { ...msgs, [k]: v };
    setMsgs(m);
    try {
      await api.put(`/partners/${p.code}`, { ...p, messages: m });
      toast.success(`${p.name}: ${types[k]} ${v ? "on" : "off"}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const uploadCert = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    file.text().then((pem) => api.post(`/as2/partner-cert?partner=${p.code}`, { pem }))
      .then(() => { toast.success("Certificate loaded"); onChanged(); }).catch((err) => toast.error(errMsg(err)));
  };
  const pdf = async () => {
    try {
      const r = await api.get("/as2/confirmation-sheet", { params: { receive_url: RECEIVE_URL, partner: p.code }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `EP_${p.code}_AS2_Connection_Confirmation.pdf`;
      a.click();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ready = !p.missing.length;
  return (
    <div className="rounded-sm border border-slate-800 bg-[#1F232C] p-5" data-testid={`partner-card-${p.code}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="font-heading text-lg text-white">{p.name}{env.env === "test" && <span className="ml-2 rounded-sm bg-amber-500/20 px-1.5 py-0.5 align-middle font-mono text-[10px] font-bold text-amber-300" data-testid={`partner-env-badge-${p.code}`}>TEST</span>}</div>
          <div className="font-mono text-xs text-slate-500">{p.code} · {p.kind === "customer" ? "Direct EDI customer" : "3PL / warehouse"}{p.builtin ? " · built-in" : ""}{p.active ? "" : " · INACTIVE"}</div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {isMgr && !p.builtin && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} className={btn.ghost} data-testid={`partner-edit-${p.code}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>}
          {isMgr && !p.builtin && (
            <label className="inline-flex cursor-pointer items-center rounded-sm px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800" data-testid={`partner-cert-label-${p.code}`}>
              <Upload className="mr-1 h-3.5 w-3.5" /> Partner certificate
              <input type="file" accept=".pem,.cer,.crt" className="hidden" onChange={uploadCert} data-testid={`partner-cert-input-${p.code}`} />
            </label>
          )}
          {isMgr && <Button size="sm" variant="ghost" onClick={() => onIdoc(p)} className={btn.ghost} data-testid={`partner-idoc-${p.code}`}><FileOutput className="mr-1 h-3.5 w-3.5" /> SAP IDoc {p.idoc?.enabled ? `on (.${p.idoc.ext})` : "off"}
            {p.idoc_waiting > 0 && <span className="ml-2 rounded-full bg-amber-500 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-950" title={`${p.idoc_waiting} order(s) waiting for a SAP IDoc`} data-testid={`partner-idoc-waiting-${p.code}`}>{p.idoc_waiting} waiting</span>}</Button>}
          {isMgr && p.kind !== "3pl" && <Button size="sm" variant="ghost" onClick={() => onPricing(p)} className={btn.ghost} data-testid={`partner-pricing-${p.code}`}>Price breaks{p.pricing?.breaks?.length ? ` (${Object.keys(p.pricing.prices || {}).length})` : ""}</Button>}
          <Button size="sm" variant="ghost" onClick={pdf} className={btn.ghost} data-testid={`partner-pdf-${p.code}`}><FileText className="mr-1 h-3.5 w-3.5" /> Confirmation PDF</Button>
        </div>
      </div>
      {!p.edi_live ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-sky-300" data-testid={`partner-status-${p.code}`}>
          <FileOutput className="h-4 w-4" />
          <span>No EDI yet - orders are entered manually (SAP IDoc only){p.manual_orders ? `, ${p.manual_orders} so far` : ""}. When EDI details arrive, add the AS2 and ISA details - SAP IDoc settings and {(p.idoc?.delivery_points || []).length} depot(s) are kept.</span>
          {isMgr && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} className={btn.ghost} data-testid={`partner-golive-${p.code}`}>Go live on EDI</Button>}
        </div>
      ) : (
        <div className={`mt-3 flex items-start gap-2 text-xs ${ready ? "text-[#22C55E]" : "text-amber-300"}`} data-testid={`partner-status-${p.code}`}>
          {ready ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {ready ? "AS2 ready" : `Missing: ${p.missing.join(", ")}`}
          {!p.builtin && p.idoc?.enabled && <span className="text-slate-400" data-testid={`partner-idoc-kept-${p.code}`}>· EDI orders use the same SAP IDoc settings and {(p.idoc.delivery_points || []).length} depot(s)</span>}
        </div>
      )}
      <div className="mt-2 text-xs text-slate-400">
        Sites: <span className="font-mono text-slate-200" data-testid={`partner-sites-${p.code}`}>{p.sites.join(", ") || "none yet - add in Partner Settings > Sites"}</span>
        {p.cert && <span className="ml-3">Certificate {p.cert.serial} valid to {dmy(p.cert.expires)}</span>}
        {p.builtin && <span className="ml-3 text-slate-500">AS2, IDs and certificate are managed in Partner Settings.</span>}
      </div>
      <EnvBox p={p} env={env} isMgr={isMgr} save={saveEnv} />
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-sm border border-slate-800 bg-[#171A21] px-3 py-2 text-xs" data-testid={`partner-send-mode-${p.code}`}>
        <span className="font-mono text-[10px] uppercase text-slate-500">Sending</span>
        <span className={auto ? "text-slate-500" : "font-semibold text-amber-300"}>Manual</span>
        <Switch checked={auto} disabled={!isMgr} onCheckedChange={setMode} data-testid={`partner-send-mode-switch-${p.code}`} />
        <span className={auto ? "font-semibold text-[#22C55E]" : "text-slate-500"}>Auto</span>
        <span className="text-slate-400" data-testid={`partner-send-mode-note-${p.code}`}>{auto ? "Outbound messages go out as soon as they are created." : "Outbound messages wait in the EDI Log until someone presses Send."}</span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(types).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-slate-300">
            <Switch checked={!!msgs[k]} disabled={!isMgr} onCheckedChange={(v) => toggle(k, v)} data-testid={`partner-${p.code}-msg-${k}`} /> {label}
          </label>
        ))}
      </div>
    </div>
  );
};
